import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import Loader from "react-loader-spinner";
import { FiArrowLeft, FiPlus, FiTrash2, FiEdit, FiUpload } from "react-icons/fi";
import AddToLeagueModal from '../components/AddToLeagueModal';
import AddPlayerModal from '../components/AddPlayerModal';
import AddRepresentativeModal from '../components/AddRepresentativeModal';
import EditPlayerModal from '../components/EditPlayerModal';
import EditPlayerNumberModal from '../components/EditPlayerNumberModal';
import ImportPlayersFromExcel from '../components/ImportPlayersFromExcel';
import EditTeamModal from '../components/EditTeamModal';
import defaultTeamLogo from "../images/defaultTeamLogo.png";
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens, logOut } from '../actions/usersActions';
import axios from 'axios';
import '../styles/manageTeam.css';

export default function ManageTeam(props) {
    const { teamId } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const userState = useSelector(state => state.user);
    const user = userState?.userData?.data || {};
    
    const [team, setTeam] = useState(null);
    const [relatedLeagues, setRelatedLeagues] = useState([]);
    const [applications, setApplications] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [loadingApplications, setLoadingApplications] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditTeamModal, setShowEditTeamModal] = useState(false);
    const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
    // Представители команды — отдельный «состав» из related_teams.
    // В селекторе он идёт как пункт REPRESENTATIVES рядом с заявками
    const [representatives, setRepresentatives] = useState([]);
    const [loadingRepresentatives, setLoadingRepresentatives] = useState(false);
    const [showAddRepresentativeModal, setShowAddRepresentativeModal] = useState(false);

    const REPRESENTATIVES = 'representatives';

    // Рут публичный, токен не нужен
    const loadRepresentatives = React.useCallback(async () => {
        if (!teamId) return;
        setLoadingRepresentatives(true);
        try {
            const url = BASE_URL.url
                + API_URLS.getTeamRepresentatives.replace(':teamId', teamId);
            const response = await axios.get(url);
            const rows = Array.isArray(response.data)
                ? response.data
                : (response.data && response.data.data) || [];
            setRepresentatives(rows);
        } catch (e) {
            console.error('Ошибка загрузки представителей команды:', e);
            setRepresentatives([]);
        } finally {
            setLoadingRepresentatives(false);
        }
    }, [teamId]);

    useEffect(() => { loadRepresentatives(); }, [loadRepresentatives]);
    const [showEditPlayerModal, setShowEditPlayerModal] = useState(false);
    const [showEditNumberModal, setShowEditNumberModal] = useState(false);
    const [showImportExcelModal, setShowImportExcelModal] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [sortBy, setSortBy] = useState('number'); // 'number', 'position', 'family'

    useEffect(() => {
        loadTeamData();
        loadApplications();
    }, [teamId]);

    // Восстанавливаем выбранную заявку из localStorage при загрузке заявок
    useEffect(() => {
        if (applications.length > 0 && selectedApplication === null) {
            const savedApplicationIndex = localStorage.getItem(`selectedApplication_${teamId}`);
            if (savedApplicationIndex !== null && applications[savedApplicationIndex]) {
                setSelectedApplication(savedApplicationIndex);
            }
        }
    }, [applications]);

    // Сохраняем выбранную заявку в localStorage при изменении
    const handleApplicationChange = (value) => {
        setSelectedApplication(value);
        if (value !== null && value !== '') {
            localStorage.setItem(`selectedApplication_${teamId}`, value);
        } else {
            localStorage.removeItem(`selectedApplication_${teamId}`);
        }
    };

    const loadTeamData = async () => {
        try {
            setLoading(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();
            
            // Загружаем данные команды
            const response = await axios.post(
                BASE_URL.url + API_URLS.getAdminTeam,
                { id: teamId },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            console.log('=== ЗАГРУЖЕНЫ ДАННЫЕ КОМАНДЫ ===');
            console.log('Team data:', JSON.stringify(response.data, null, 2));
            
            if (response.data) {
                setTeam(response.data);
                setRelatedLeagues(response.data.related_leagues || []);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error loading team:', error);
            setError('Ошибка загрузки данных команды');
            setLoading(false);
            
            if (error.response?.status === 401) {
                dispatch(logOut());
            }
        }
    };

    const loadApplications = async () => {
        try {
            setLoadingApplications(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();
            
            // Загружаем заявки с игроками (как было раньше)
            const response = await axios.get(
                BASE_URL.url + `/teams/${teamId}/applicationsWithActiveProfiles`,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            // Загружаем ВСЕ заявки команды через админский endpoint (включая пустые)
            const allAppsResponse = await axios.post(
                BASE_URL.url + '/teams/getAdminApplications',
                { team_id: teamId },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            // Добавляем timestamp к изображениям игроков для обхода кэша
            const addTimestampToImages = (applications) => {
                return applications.map(app => {
                    if (app.data && Array.isArray(app.data)) {
                        app.data = app.data.map(player => {
                            if (player.image && player.image.startsWith('/')) {
                                // Убираем старый timestamp и добавляем новый
                                player.image = player.image.split('?')[0] + '?v=' + Date.now();
                            }
                            return player;
                        });
                    }
                    return app;
                });
            };
            
            let appsWithPlayers = [];
            if (Array.isArray(response.data)) {
                appsWithPlayers = addTimestampToImages(response.data);
            } else if (response.data && response.data.rosters) {
                appsWithPlayers = addTimestampToImages(response.data.rosters);
            } else if (response.data && response.data.applications) {
                appsWithPlayers = addTimestampToImages(response.data.applications);
            } else if (response.data && response.data.data) {
                appsWithPlayers = addTimestampToImages(response.data.data);
            }
            
            // Получаем все заявки
            let allApps = [];
            if (Array.isArray(allAppsResponse.data)) {
                allApps = allAppsResponse.data;
            } else if (allAppsResponse.data?.applications) {
                allApps = allAppsResponse.data.applications;
            } else if (allAppsResponse.data?.data) {
                allApps = allAppsResponse.data.data;
            }
            
            // Создаём Map для быстрого поиска по application_id
            const appsMap = new Map();
            
            // Сначала добавляем все заявки с игроками
            appsWithPlayers.forEach(app => {
                const appId = app.application_id || app.id;
                appsMap.set(appId, app);
            });
            
            // Затем добавляем заявки без игроков (если их ещё нет в Map)
            allApps.forEach(app => {
                const appId = app.application_id || app.id;
                if (!appsMap.has(appId)) {
                    appsMap.set(appId, {
                        ...app,
                        data: []
                    });
                }
            });
            
            // Преобразуем Map в массив
            const mergedApplications = Array.from(appsMap.values());
            
            setApplications(mergedApplications);
            setLoadingApplications(false);
        } catch (error) {
            console.error('Error loading applications:', error);
            setLoadingApplications(false);
        }
    };

    const handleBack = () => {
        history.push('/teams_management');
    };

    const getTeamLogo = () => {
        if (!team) return defaultTeamLogo;
        
        const logo = team.image || team.logo_url || team.logo || team.team_logo || '';
        
        if (!logo || logo === '' || logo === '/images/others/defaultLogo.png') {
            return defaultTeamLogo;
        }
        
        if (logo.startsWith('http')) {
            return logo;
        }
        
        // Добавляем timestamp для обхода кэша браузера
        const logoUrl = BASE_URL.url + logo;
        const separator = logoUrl.includes('?') ? '&' : '?';
        return logoUrl + separator + 'v=' + Date.now();
    };

    const handleRemoveLeague = async (leagueId, seasonId) => {
        if (!window.confirm('Вы уверены, что хотите отключить команду от этой лиги?')) {
            return;
        }
        
        try {
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();
            
            console.log('Removing team from league:', { teamId, leagueId, seasonId });
            
            // Формат данных: оба массива, один заполнен, другой пустой
            const requestData = {
                add_data: [],
                remove_data: [{
                    league_id: parseInt(leagueId),
                    season_id: parseInt(seasonId)
                }],
                teamId: parseInt(teamId)
            };
            
            console.log('Remove request data:', requestData);
            console.log('Sending request to:', BASE_URL.url + API_URLS.connectToLeague);
            
            const response = await axios.post(
                BASE_URL.url + API_URLS.connectToLeague,
                requestData,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    },
                    timeout: 30000
                }
            );
            
            console.log('Remove response status:', response.status);
            console.log('Remove response data:', response.data);
            console.log('Removed successfully!');
            
            alert('Команда успешно отключена от лиги!');
            
            // После успешного удаления перезагружаем данные
            await loadTeamData();
            
        } catch (error) {
            console.error('!!! CAUGHT ERROR removing league:', error);
            console.error('Error name:', error.name);
            console.error('Error message:', error.message);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            
            let errorMessage = 'Ошибка при отключении от лиги';
            
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                errorMessage = 'Превышено время ожидания ответа сервера (30 сек). Проверьте работу endpoint /teams/connectToLeague на бэкенде.';
            } else if (error.message === 'Network Error') {
                errorMessage = 'Ошибка сети. Сервер недоступен.';
            } else if (error.response) {
                errorMessage = error.response?.data?.message 
                    || error.response?.data?.error 
                    || `Ошибка сервера: ${error.response.status}`;
            } else {
                errorMessage = error.message || errorMessage;
            }
            
            alert('Ошибка при удалении из лиги:\n' + errorMessage);
        }
    };

    const handleEditTeam = () => {
        setShowEditTeamModal(true);
    };

    const handleSaveTeam = async (teamData) => {
        try {
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }

            const tokens = getTokensFromStore();
            
            console.log('Starting team update for team ID:', teamId);
            console.log('Team data to save:', teamData);
            
            // Формируем объект teamData как в мобильном приложении
            const teamDataObject = {
                name: teamData.name,
                short_name: teamData.shortName || '',
                image: false,  // по умолчанию false, если не меняем
                timestamp: Date.now()  // Добавляем timestamp для уникального имени файла
            };
            
            // Если нужно установить дефолтный логотип
            if (teamData.shouldDeleteLogo) {
                console.log('Setting default team logo');
                teamDataObject.image = 'default';  // 'default' для установки дефолтного логотипа
            }
            // Если загружен новый логотип
            else if (teamData.logoFile) {
                console.log('Processing logo file:', teamData.logoFile.name);
                
                // Конвертируем изображение в base64
                const reader = new FileReader();
                
                const base64Promise = new Promise((resolve, reject) => {
                    reader.onloadend = () => {
                        resolve(reader.result);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(teamData.logoFile);
                });
                
                const base64Image = await base64Promise;
                
                // Убираем префикс "data:image/png;base64," или "data:image/jpeg;base64," и т.д.
                const base64Data = base64Image.split(',')[1];
                
                console.log('Image converted to base64, length:', base64Data.length);
                
                // Добавляем изображение в формате base64
                teamDataObject.image = base64Data;
            }

            // Формат запроса как в мобильном приложении: { id: teamId, data: teamData }
            const requestData = {
                id: parseInt(teamId),
                data: teamDataObject
            };

            console.log('Sending request to /teams/changeTeam with data:', {
                id: requestData.id,
                data: {
                    name: teamDataObject.name,
                    short_name: teamDataObject.short_name,
                    timestamp: teamDataObject.timestamp,
                    image: teamDataObject.image === false 
                        ? 'false (no change)' 
                        : teamDataObject.image === 'default' 
                            ? 'default (set default logo)' 
                            : `base64 string (${teamDataObject.image.length} chars)`
                }
            });

            console.log('=== ОТПРАВКА ЗАПРОСА ===');
            console.log('URL:', BASE_URL.url + '/teams/changeTeam');
            console.log('Full request data:', JSON.stringify(requestData, null, 2));
            
            const response = await axios.post(
                BASE_URL.url + '/teams/changeTeam',
                requestData,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );

            console.log('=== ОТВЕТ ОТ СЕРВЕРА ===');
            console.log('Response status:', response.status);
            console.log('Response data:', JSON.stringify(response.data, null, 2));

            alert('Команда успешно обновлена!');
            setShowEditTeamModal(false);
            
            // Перезагружаем данные команды
            console.log('Перезагрузка данных команды...');
            await loadTeamData();
            console.log('Данные команды перезагружены');
        } catch (error) {
            console.error('!!! ERROR updating team !!!');
            console.error('Error object:', error);
            console.error('Error response:', error.response);
            console.error('Error response data:', error.response?.data);
            console.error('Error response status:', error.response?.status);
            console.error('Error message:', error.message);
            
            let errorMessage = 'Ошибка при обновлении команды';
            
            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else {
                    errorMessage = JSON.stringify(error.response.data);
                }
            }
            
            throw new Error(errorMessage);
        }
    };

    const handleAddLeague = () => {
        setShowAddModal(true);
    };

    const handleModalSuccess = () => {
        // Перезагружаем данные команды после успешного добавления
        loadTeamData();
    };

    const handleRemovePlayer = async (playerId, applicationId) => {
        if (!window.confirm("Вы уверены, что хотите отзаявить этого игрока?")) {
            return;
        }

        try {
            setLoading(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();
            
            // Используем тот же endpoint что и для добавления, но с remove_data
            // Формат данных: {id: team.id, application_id: application_id}
            const requestData = {
                playerId: playerId,
                add_data: [],
                remove_data: [{
                    id: teamId,
                    application_id: applicationId
                }]
            };
            
            const response = await axios.post(
                BASE_URL.url + '/players/connectToApplication',
                requestData,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );

            alert('Игрок успешно отзаявлен!');
            
            // Перезагружаем список заявок
            await loadApplications();
        } catch (error) {
            console.error('Error removing player:', error);
            let errorMessage = 'Произошла ошибка при отзаявке игрока';
            
            if (error.response) {
                errorMessage = error.response?.data?.message 
                    || error.response?.data?.error 
                    || error.response?.data
                    || `Ошибка сервера: ${error.response.status}`;
            }
            
            alert('Ошибка при отзаявке игрока:\n' + errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Функция сортировки игроков
    const getSortedPlayers = (players) => {
        if (!players || players.length === 0) return [];
        
        const playersCopy = [...players];
        
        switch (sortBy) {
            case 'number':
                return playersCopy.sort((a, b) => {
                    const numA = a.player_number || a.number || a.playerNumber || 999;
                    const numB = b.player_number || b.number || b.playerNumber || 999;
                    return numA - numB;
                });
            
            case 'position':
                return playersCopy.sort((a, b) => {
                    const posA = a.position || 'Я';
                    const posB = b.position || 'Я';
                    return posA.localeCompare(posB, 'ru');
                });
            
            case 'family':
                return playersCopy.sort((a, b) => {
                    const familyA = a.family || '';
                    const familyB = b.family || '';
                    return familyA.localeCompare(familyB, 'ru');
                });
            
            default:
                return playersCopy;
        }
    };

    const handlePlayerClick = (playerId) => {
        history.push(`/player/${playerId}`);
    };

    const handleEditPlayer = (player) => {
        // Получаем данные текущего пользователя (user уже распакован)
        if (!user || !user.role) {
            alert('Ошибка: данные пользователя не загружены');
            console.error('User data:', user);
            return;
        }

        // Проверяем права на редактирование
        // role == 7 - это супер админ, может редактировать всех
        // role == 6 - админ лиги, может редактировать только своих игроков (client_owner совпадает)
        if (user.role === 7) {
            // Супер админ может редактировать любого игрока
            setSelectedPlayer(player);
            setShowEditPlayerModal(true);
        } else if (user.role === 6 && user.client_id === player.client_owner) {
            // Админ лиги может редактировать только игроков своей лиги
            setSelectedPlayer(player);
            setShowEditPlayerModal(true);
        } else {
            // Нет прав на редактирование
            alert('Вы можете менять данные только созданных Вашей лигой игроков. Для изменения данных обратитесь к разработчикам приложения');
        }
    };

    const handlePlayerSaved = () => {
        // Перезагружаем данные после сохранения
        loadApplications();
    };

    const handleEditPlayerNumber = (player) => {
        setSelectedPlayer(player);
        setShowEditNumberModal(true);
    };

    const handleSavePlayerNumber = async (newNumber) => {
        if (!selectedPlayer || !applications[selectedApplication]) {
            alert('Отсутствуют необходимые данные');
            return;
        }

        try {
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }

            const tokens = getTokensFromStore();
            const playerId = selectedPlayer.id || selectedPlayer.player_id || selectedPlayer.playerId;
            const applicationId = applications[selectedApplication].application_id;

            // Валидация и преобразование номера
            const playerNumber = parseInt(newNumber);
            
            if (isNaN(playerNumber) || playerNumber < 0 || playerNumber > 99) {
                alert('Некорректный номер игрока');
                return;
            }

            // Шаг 1: Сначала отзаявляем игрока
            await axios.post(
                BASE_URL.url + '/players/connectToApplication',
                {
                    playerId: playerId,
                    add_data: [],
                    remove_data: [{
                        id: parseInt(teamId),
                        application_id: applicationId
                    }]
                },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            // Шаг 2: Теперь заявляем его обратно с новым номером.
            // Шаги не атомарны: если второй упадёт, игрок останется отзаявленным —
            // поэтому при ошибке возвращаем его со старым номером
            const application = applications[selectedApplication];
            const reconnect = (number) => axios.post(
                BASE_URL.url + '/players/connectToApplication',
                {
                    playerId: playerId,
                    add_data: [{
                        id: parseInt(teamId),
                        name: team.name,
                        player_number: number,
                        application_id: applicationId,
                        application_name: application.application_name,
                        application_comment: application.application_comment,
                        application_year: application.application_year || application.year || new Date().getFullYear()
                    }],
                    remove_data: []
                },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );
            try {
                await reconnect(playerNumber);
            } catch (stepError) {
                const oldRaw = selectedPlayer.player_number || selectedPlayer.number || selectedPlayer.playerNumber;
                const oldNumber = oldRaw === undefined || oldRaw === null || oldRaw === '' ? null : parseInt(oldRaw, 10);
                try {
                    await reconnect(oldNumber);
                } catch (restoreError) {
                    await loadApplications();
                    alert('Номер не изменён, и игрока не удалось вернуть в заявку. Заявите его заново.');
                    return;
                }
                await loadApplications();
                throw stepError;
            }

            alert('Номер успешно изменен!');

            // Перезагружаем данные
            await loadApplications();
            setShowEditNumberModal(false);
            setSelectedPlayer(null);
        } catch (error) {
            console.error('Ошибка изменения номера:', error);
            
            let errorMessage = 'Произошла ошибка при изменении номера игрока';
            
            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                }
            }
            
            alert(errorMessage);
        }
    };


    if (loading) {
        return (
            <div className="layoutWrapper">
                <div className="loaderWrapper">
                    <Loader
                        type="Oval"
                        color="#4A5568"
                        height={80}
                        width={80}
                    />
                    <p style={{marginTop: '20px', color: '#718096'}}>Загрузка данных команды...</p>
                </div>
            </div>
        );
    }

    if (error || !team) {
        return (
            <div className="layoutWrapper">
                <div className="errorMessage">
                    <p>{error || 'Команда не найдена'}</p>
                    <button onClick={handleBack} className="backButton">
                        Вернуться к списку команд
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="layoutWrapper">
            <div className="manageTeamWrapper">
                <div className="manageTeamHeader">
                    <button className="backButton" onClick={handleBack}>
                        <FiArrowLeft /> Назад к командам
                    </button>
                </div>

                <div className="teamInfoCard">
                    <div className="teamInfoHeader">
                        <img 
                            src={getTeamLogo()} 
                            alt={team.name}
                            className="teamInfoLogo"
                            onError={(e) => { e.target.src = defaultTeamLogo; }}
                        />
                        <div className="teamInfoDetails">
                            <h1 className="teamInfoName">{team.name}</h1>
                            {team.short_name && (
                                <span className="teamInfoShortName">{team.short_name}</span>
                            )}
                            <span className="teamInfoId">ID: {team.id}</span>
                        </div>
                        <button 
                            className="editTeamButton" 
                            onClick={handleEditTeam}
                            title="Редактировать команду"
                        >
                            <FiEdit /> Редактировать
                        </button>
                    </div>
                </div>

                <div className="relatedLeaguesSection">
                    <div className="relatedLeaguesHeader">
                        <h2>Лиги, в которых играет команда</h2>
                        <button className="addLeagueButton" onClick={handleAddLeague}>
                            <FiPlus /> Добавить в лигу
                        </button>
                    </div>

                    {relatedLeagues.length > 0 ? (
                        <div className="leaguesList">
                            {relatedLeagues.map((league, index) => (
                                <div key={index} className="leagueCard">
                                    <div className="leagueCardContent">
                                        <div className="leagueCardInfo">
                                            <h3 className="leagueName">{league.league_name}</h3>
                                            <div className="leagueDetails">
                                                <span className="leagueDetail">
                                                    <strong>Чемпионат:</strong> {league.championat_name}
                                                </span>
                                                <span className="leagueDetail">
                                                    <strong>Сезон:</strong> {league.season_name || league.season || league.season_id}
                                                </span>
                                            </div>
                                        </div>
                                        <button 
                                            className="removeLeagueButton"
                                            onClick={() => handleRemoveLeague(league.league_id, league.season_id)}
                                            title="Отключить от лиги"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="emptyLeagues">
                            <p>Команда пока не присоединена ни к одной лиге</p>
                            <button className="addLeagueButtonLarge" onClick={handleAddLeague}>
                                <FiPlus /> Добавить команду в лигу
                            </button>
                        </div>
                    )}
                </div>

                {/* Секция заявок команды */}
                <div className="relatedLeaguesSection">
                    <div className="relatedLeaguesHeader">
                        <h2>Состав команды</h2>
                        {selectedApplication === REPRESENTATIVES ? (
                            <button className="addLeagueButton" onClick={() => setShowAddRepresentativeModal(true)}>
                                <FiPlus /> Добавить представителя
                            </button>
                        ) : selectedApplication !== null && (
                            <div style={{display: 'flex', gap: '12px'}}>
                                <button className="addLeagueButton" onClick={() => setShowAddPlayerModal(true)}>
                                    <FiPlus /> Добавить игрока
                                </button>
                                <button className="addLeagueButton" onClick={() => setShowImportExcelModal(true)} style={{background: '#38a169'}}>
                                    <FiUpload /> Добавить из Excel
                                </button>
                            </div>
                        )}
                    </div>

                    {loadingApplications ? (
                        <div className="loaderWrapper" style={{padding: '20px'}}>
                            <Loader
                                type="Oval"
                                color="#4A5568"
                                height={40}
                                width={40}
                            />
                        </div>
                    ) : applications.length > 0 ? (
                        <>
                            <div className="applicationSelector">
                                <label>Состав или заявка:</label>
                                <select 
                                    value={selectedApplication || ''}
                                    onChange={(e) => handleApplicationChange(e.target.value)}
                                >
                                    <option value="">Выберите состав</option>
                                    <option value={REPRESENTATIVES}>
                                        Представители команды ({representatives.length})
                                    </option>
                                    {applications.map((app, index) => (
                                        <option key={index} value={index}>
                                            {app.label || app.application_name || `Заявка #${app.application_id}`} ({app.data?.length || 0} игроков)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedApplication === REPRESENTATIVES ? (
                                <div className="representativesBlock">
                                    {loadingRepresentatives ? (
                                        <div className="loaderWrapper" style={{padding: '20px'}}>
                                            <Loader type="Oval" color="#4A5568" height={40} width={40} />
                                        </div>
                                    ) : representatives.length === 0 ? (
                                        <p className="emptyState">
                                            В команде пока нет представителей.
                                        </p>
                                    ) : (
                                        <div className="representativesList">
                                            {representatives.map(person => (
                                                <div
                                                    key={person.id}
                                                    className="representativeRow"
                                                    onClick={() => history.push(`/player/${person.id}`)}
                                                >
                                                    <span className="representativeNumber">
                                                        {person.number || '—'}
                                                    </span>
                                                    <span className="representativeMain">
                                                        <span className="representativeName">
                                                            {person.family} {person.name}
                                                        </span>
                                                        <span className="representativeMeta">
                                                            {person.position || ''}
                                                            {person.application_name
                                                                ? ` · ${person.application_name}` : ''}
                                                        </span>
                                                    </span>
                                                    {Number(person.capitan) === 1 ? (
                                                        <span className="representativeBadge">капитан</span>
                                                    ) : null}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : selectedApplication !== null && applications[selectedApplication] ? (
                                <>
                                    <div className="sortSelector" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        marginBottom: '20px',
                                        padding: '15px',
                                        background: '#f7fafc',
                                        borderRadius: '8px'
                                    }}>
                                        <label style={{fontWeight: '500', color: '#2d3748'}}>Сортировка:</label>
                                        <select 
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e0',
                                                background: 'white',
                                                fontSize: '14px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <option value="number">По игровому номеру</option>
                                            <option value="position">По позиции</option>
                                            <option value="family">По фамилии</option>
                                        </select>
                                    </div>

                                    <div className="playersGrid">
                                        {applications[selectedApplication].data && applications[selectedApplication].data.length > 0 ? (
                                            getSortedPlayers(applications[selectedApplication].data).map((player, pIndex) => {
                                            // Определяем player_id - может быть в разных полях
                                            const playerId = player.player_id || player.id || player.user_id;
                                            // Определяем номер игрока - может быть в разных полях
                                            const playerNumber = player.player_number || player.number || player.playerNumber;
                                            
                                            return (
                                            <div 
                                                key={pIndex} 
                                                className="playerCard"
                                                onClick={() => playerId && handlePlayerClick(playerId)}
                                                style={{ cursor: playerId ? 'pointer' : 'default' }}
                                            >
                                                {playerNumber && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '12px',
                                                        right: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        zIndex: 1
                                                    }}>
                                                        <div className="playerNumberBadge">
                                                            #{playerNumber}
                                                        </div>
                                                        <button
                                                            className="editNumberButton"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditPlayerNumber(player);
                                                            }}
                                                            title="Изменить номер"
                                                            style={{
                                                                background: 'white',
                                                                border: '2px solid #3182ce',
                                                                color: '#3182ce',
                                                                borderRadius: '50%',
                                                                width: '32px',
                                                                height: '32px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                padding: 0,
                                                                flexShrink: 0
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.background = '#3182ce';
                                                                e.currentTarget.style.color = 'white';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.background = 'white';
                                                                e.currentTarget.style.color = '#3182ce';
                                                            }}
                                                        >
                                                            <FiEdit size={16} />
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="playerPhoto">
                                                    <img 
                                                        src={
                                                            player.image 
                                                                ? (player.image.startsWith('/') 
                                                                    ? BASE_URL.url + player.image 
                                                                    : player.image.startsWith('data:') 
                                                                        ? player.image 
                                                                        : `data:image/jpeg;base64,${player.image}`)
                                                                : defaultTeamLogo
                                                        }
                                                        alt={player.family || 'Игрок'}
                                                        onError={(e) => { e.target.src = defaultTeamLogo; }}
                                                    />
                                                </div>
                                                <div className="playerInfo">
                                                    <h4 className="playerFullName">
                                                        {player.family} {player.name} {player.father_name}
                                                    </h4>
                                                    <div className="playerDetails">
                                                        {player.position && (
                                                            <span className="playerDetail">
                                                                <strong>Позиция:</strong> {player.position}
                                                            </span>
                                                        )}
                                                        {player.birthday && (
                                                            <span className="playerDetail">
                                                                <strong>Дата рождения:</strong> {new Date(player.birthday).toLocaleDateString('ru-RU')}
                                                            </span>
                                                        )}
                                                        {playerId && (
                                                            <span className="playerDetail playerIdBadge">
                                                                ID: {playerId}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="playerActions">
                                                        <button 
                                                            className="editPlayerButton"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditPlayer(player);
                                                            }}
                                                            title="Редактировать игрока"
                                                        >
                                                            <FiEdit /> Редактировать
                                                        </button>
                                                        <button 
                                                            className="removePlayerButton"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemovePlayer(playerId, applications[selectedApplication].application_id);
                                                            }}
                                                            title="Отзаявить игрока"
                                                        >
                                                            <FiTrash2 /> Отзаявить
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })
                                    ) : (
                                        <p className="noPlayers">Нет игроков в этой заявке</p>
                                    )}
                                    </div>
                                </>
                            ) : (
                                <div className="emptySelection">
                                    <p>Выберите заявку для просмотра состава</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="emptyLeagues">
                            <p>У команды пока нет заявок</p>
                        </div>
                    )}
                </div>
            </div>
            
            <AddToLeagueModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleModalSuccess}
                teamId={teamId}
            />

            <EditTeamModal
                isOpen={showEditTeamModal}
                onClose={() => setShowEditTeamModal(false)}
                onSave={handleSaveTeam}
                team={team}
            />

            <AddRepresentativeModal
                isOpen={showAddRepresentativeModal}
                onClose={() => setShowAddRepresentativeModal(false)}
                onSuccess={() => {
                    setShowAddRepresentativeModal(false);
                    loadRepresentatives();
                }}
                team={team}
                teamId={teamId}
            />

            <AddPlayerModal
                isOpen={showAddPlayerModal}
                onClose={() => setShowAddPlayerModal(false)}
                onSuccess={loadApplications}
                applicationId={selectedApplication !== null ? applications[selectedApplication]?.application_id : null}
                application={selectedApplication !== null ? applications[selectedApplication] : null}
                team={team}
                teamId={teamId}
                occupiedNumbers={selectedApplication !== null && applications[selectedApplication]?.data 
                    ? applications[selectedApplication].data.map(p => p.player_number).filter(n => n)
                    : []
                }
            />

            <EditPlayerModal
                isOpen={showEditPlayerModal}
                onClose={() => {
                    setShowEditPlayerModal(false);
                    setSelectedPlayer(null);
                }}
                onSuccess={handlePlayerSaved}
                player={selectedPlayer}
                isCreateMode={false}
                userData={user}
            />

            <ImportPlayersFromExcel
                isOpen={showImportExcelModal}
                onClose={() => setShowImportExcelModal(false)}
                onSuccess={loadApplications}
                applicationId={selectedApplication !== null ? applications[selectedApplication]?.application_id : null}
                application={selectedApplication !== null ? applications[selectedApplication] : null}
                team={team}
                teamId={teamId}
                occupiedNumbers={selectedApplication !== null && applications[selectedApplication]?.data 
                    ? applications[selectedApplication].data.map(p => p.player_number).filter(n => n)
                    : []
                }
                userData={user}
            />

            <EditPlayerNumberModal
                isOpen={showEditNumberModal}
                onClose={() => {
                    setShowEditNumberModal(false);
                    setSelectedPlayer(null);
                }}
                player={selectedPlayer}
                currentNumber={selectedPlayer?.player_number || selectedPlayer?.number || selectedPlayer?.playerNumber}
                occupiedNumbers={selectedApplication !== null && applications[selectedApplication]?.data 
                    ? applications[selectedApplication].data
                        .map(p => p.player_number || p.number || p.playerNumber)
                        .filter(n => n && n !== (selectedPlayer?.player_number || selectedPlayer?.number || selectedPlayer?.playerNumber))
                        .map(n => parseInt(n))
                    : []
                }
                onSave={handleSavePlayerNumber}
            />
        </div>
    );
}
